import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { hdmiPort } from './TopologyOverview';
import VisualCheckpointEditor from './VisualCheckpointEditor';
import './AreaOverview.css';
import { INPUT_CONTROL_METHODS, DEFAULT_INPUT_CONTROL_METHODS, inputTrainingActionBody,
  inputDeliverySummary, inputFailureSummary, inputControlMethodLabel } from './inputTrainingControlMethods';
import {
  createWaitToken,
  formatSequenceStep,
  isWaitToken,
  normalizeWaitMs,
  profileStepFromToken,
  sequenceDelayMs,
  tokensFromProfileSteps,
  waitMsFromToken,
} from './automationSequenceSteps';

const ACTIONS = ['input', 'settings', 'home', 'back', 'up', 'down', 'left', 'right', 'enter'];

function profileFor(screen, player, television, actions, stepCheckpoints, stepDelayMs, destination, inputSelectorMethod, wireless = false, controlMethods = null) {
  const expectedPort = destination === 'home' ? null : Number(destination) || null;
  const appNode = television?.integrationData?.appNode || television?.integration_data?.appNode || {};
  const steps = actions.map((action, index) => (
    profileStepFromToken(action, stepDelayMs, stepCheckpoints[index])
  ));
  return {
    id: wireless ? 'wireless-debugging-recovery' : routineIdForDestination(destination),
    name: wireless ? `${television?.name || 'Device'} · Wireless Debugging` : destination === 'home'
      ? `${television?.name ? `${television.name} · ` : ''}TV Home`
      : `${player?.name ? `${player.name} · ` : ''}HDMI ${expectedPort}`,
    goal: wireless ? 'wireless_debugging' : 'input_selector',
    ...(wireless ? { targetDeviceId: Number(screen.televisionDeviceId) } : {}),
    inputSelectorMethod,
    ...(!wireless && controlMethods ? { controlMethods } : {}),
    trainingState: 'draft',
    deviceFingerprint: {
      manufacturer: appNode.manufacturer || null,
      model: appNode.model || television?.model || null,
      androidRelease: appNode.androidRelease || null,
    },
    steps,
    finalVerification: {
      id: wireless ? 'adb-reconnected' : 'requested-hdmi-active',
      match: 'all',
      observations: [wireless ? { type: 'adb_connected' } : destination === 'home'
        ? { type: 'tv_home' }
        : { type: 'tv_active_input', expectedPort }],
      maxAttempts: wireless ? 1 : 3,
      retryDelayMs: 1000,
      recoverySteps: [],
    },
    limits: {
      maxDurationMs: Math.max(30000, Math.min(120000, sequenceDelayMs(actions, stepDelayMs) + 15000)),
      maxActions: 30,
    },
  };
}

function resultSummary(result, wireless = false) {
  if (!result) return null;
  if (result.status === 'planned') {
    return `${result.plan?.actionCount || 0} action(s); ${result.plan?.executable ? 'ready for a controlled test' : 'not executable'}.`;
  }
  if (!wireless && result.status === 'verified') return `Verified after ${result.actionsSent} remote action(s).`;
  if (result.status === 'checkpoint_failed') return `Stopped safely at checkpoint ${result.failedCheckpointId}.`;
  if (wireless && result.status === 'verified') return 'Wireless Debugging connected — ADB responded successfully.';
  if (wireless && result.status === 'verification_failed') return 'Recovery keys sent; ADB has not reconnected yet. Automatic connection checks will continue.';
  if (result.status === 'verification_failed') return 'Commands completed, but the requested HDMI input was not verified.';
  return result.status || 'Completed';
}

function savedProfilePort(profile) {
  return Number(profile?.finalVerification?.observations
    ?.find((observation) => observation.type === 'tv_active_input')?.expectedPort) || null;
}

export function routineIdForPort(port) {
  return `trained-input-selector-hdmi-${Number(port)}`;
}

export function profileInputDestination(profile) {
  const observations = profile?.finalVerification?.observations || [];
  if (observations.some((observation) => observation.type === 'tv_home')) return 'home';
  return savedProfilePort(profile);
}

export function routineIdForDestination(destination) {
  return destination === 'home' ? 'trained-input-selector-tv-home' : routineIdForPort(destination);
}

function destinationLabel(destination) {
  return destination === 'home' ? 'TV Home' : `HDMI ${destination}`;
}

export function upsertInputProfileByPort(profiles, candidate) {
  const inputProfilesByDestination = new Map();
  const otherProfiles = [];
  (Array.isArray(profiles) ? profiles : []).forEach((entry) => {
    if (entry?.goal !== 'input_selector') {
      otherProfiles.push(entry);
      return;
    }
    const destination = profileInputDestination(entry);
    if (destination) inputProfilesByDestination.set(destination, { ...entry, id: routineIdForDestination(destination) });
  });
  const candidateDestination = profileInputDestination(candidate);
  if (!candidateDestination) return [...otherProfiles, ...inputProfilesByDestination.values()];
  inputProfilesByDestination.set(candidateDestination, { ...candidate, id: routineIdForDestination(candidateDestination) });
  return [
    ...otherProfiles,
    ...[...inputProfilesByDestination.entries()]
      .sort(([left], [right]) => {
        if (left === 'home') return 1;
        if (right === 'home') return -1;
        return left - right;
      })
      .map(([, entry]) => entry),
  ];
}

export default function InputAutomationTrainer({ areaId, topology, tvInputReports = {}, devices = [], siteAreas = [], initialScreenId = '', initialPort = null, onTopologyChange, onConfigureScreen, recoveryTarget = null, savedRecoveryProfile = null, onSaveRecovery, recoveryTargets = [], onRecoveryBusyChange }) {
  const wireless = Boolean(recoveryTarget);
  const recoveryScreen = useMemo(() => recoveryTarget ? { id: recoveryTarget.id, name: recoveryTarget.name,
    televisionDeviceId: recoveryTarget.id, players: [] } : null, [recoveryTarget]);
  const managedScreens = wireless ? [recoveryScreen] : (topology?.screens || []).filter((screen) => screen.displayKind !== 'unmanaged' && screen.televisionDeviceId);
  const [screenId, setScreenId] = useState(String(initialScreenId || ''));
  const [targetPort, setTargetPort] = useState(String(initialPort || ''));
  const [actions, setActions] = useState([]);
  const [stepCheckpoints, setStepCheckpoints] = useState([]);
  const [stepDelayMs, setStepDelayMs] = useState(600);
  const [sendWhileRecording, setSendWhileRecording] = useState(true);
  const [inputSelectorMethod, setInputSelectorMethod] = useState('control_order');
  const [controlMethods, setControlMethods] = useState(DEFAULT_INPUT_CONTROL_METHODS);
  const [liveAction, setLiveAction] = useState('');
  const [busy, setBusy] = useState('');
  const [message, setMessage] = useState('');
  const [result, setResult] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [clonePort, setClonePort] = useState('');
  const [copyAreaId, setCopyAreaId] = useState('');
  const [copyBusy, setCopyBusy] = useState(false);
  const [checkpointEditor, setCheckpointEditor] = useState(null);
  const [stepIndex, setStepIndex] = useState(null);
  useEffect(() => { onRecoveryBusyChange?.(Boolean(busy || liveAction)); }, [busy, liveAction, onRecoveryBusyChange]);
  const screen = managedScreens.find((entry) => String(entry.id) === String(screenId)) || managedScreens[0] || null;
  const savedProfiles = useMemo(
    () => wireless ? (savedRecoveryProfile ? [savedRecoveryProfile] : [])
      : (screen?.activationStrategy?.automationProfiles || []).filter((entry) => entry.goal === 'input_selector'),
    [screen, wireless, savedRecoveryProfile]
  );
  const players = screen?.players || [];
  const availablePorts = useMemo(() => {
    if (!screen) return [];
    const report = tvInputReports[Number(screen.televisionDeviceId)];
    const inputs = Array.isArray(report?.inputs) ? report.inputs : [];
    const inputsById = new Map(inputs.map((input) => [input.id, input]));
    const reportedPorts = inputs.flatMap((input) => {
      const parent = input.parentId ? inputsById.get(input.parentId) : null;
      const port = hdmiPort(parent?.label) || hdmiPort(input.label);
      return port ? [port] : [];
    });
    const mappedPorts = players.map((entry) => hdmiPort(entry.expectedInput)).filter(Boolean);
    const savedPorts = savedProfiles.map(savedProfilePort).filter(Boolean);
    return [...new Set([...reportedPorts, ...mappedPorts, ...savedPorts])].sort((a, b) => a - b);
  }, [players, savedProfiles, screen, tvInputReports]);
  const destination = wireless ? 'wireless' : targetPort === 'home' ? 'home' : Number(targetPort) || availablePorts[0] || null;
  const expectedPort = wireless || destination === 'home' ? null : destination;
  const savedProfile = savedProfiles.find((entry) => wireless || profileInputDestination(entry) === destination) || null;
  const player = players.find((entry) => hdmiPort(entry.expectedInput) === expectedPort) || null;
  const television = recoveryTarget || devices.find((entry) => Number(entry.id) === Number(screen?.televisionDeviceId)) || null;
  const profile = useMemo(
    () => screen ? profileFor(screen, player, television, actions, stepCheckpoints, stepDelayMs, destination, inputSelectorMethod, wireless, controlMethods) : null,
    [actions, destination, inputSelectorMethod, player, screen, stepCheckpoints, stepDelayMs, television, wireless, controlMethods]
  );
  const liveActiveSource = tvInputReports[Number(screen?.televisionDeviceId)]?.activeSource;
  const liveInputConfirmed = Boolean(expectedPort
    && liveActiveSource?.available
    && Number(liveActiveSource.port) === expectedPort);
  const executionCompleted = Boolean(result && result.status !== 'planned' && Number(result.actionsSent) > 0);
  const telemetryConfirmed = result?.status === 'verified' || (executionCompleted && liveInputConfirmed);

  const loadProfile = (savedProfile) => {
    if (!savedProfile) return;
    const sequence = tokensFromProfileSteps(savedProfile.steps);
    setActions(sequence.tokens);
    setStepCheckpoints(sequence.checkpoints);
    const firstCommand = (savedProfile.steps || []).find((step) => step.action !== 'wait');
    setStepDelayMs(Number(firstCommand?.delayAfterMs) || 600);
    setInputSelectorMethod(savedProfile.inputSelectorMethod || (savedProfile.controlMethods ? 'control_order' : 'suitestream_intent'));
    setControlMethods(savedProfile.controlMethods || null);
    setResult(null);
    setStepIndex(null);
    setMessage(`${savedProfile.name || savedProfile.id} loaded.`);
    setHasUnsavedChanges(false);
  };

  useEffect(() => {
    if (!screen) return;
    setTargetPort(String(initialPort || availablePorts[0] || ''));
  }, [screen?.televisionDeviceId]);

  useEffect(() => {
    if (!screen || !destination) return;
    const matchingProfile = savedProfiles.find((entry) => wireless || profileInputDestination(entry) === destination);
    if (matchingProfile) {
      loadProfile(matchingProfile);
      return;
    }
    setActions([]);
    setStepCheckpoints([]);
    setControlMethods(television?.type === 'lgtv' || screen.inputReport?.connector === 'lg_webos_remote' ? ['lg_webos_remote'] : DEFAULT_INPUT_CONTROL_METHODS);
    setInputSelectorMethod('control_order');
    setStepIndex(null);
    setResult(null);
    setHasUnsavedChanges(false);
    setMessage(wireless ? 'No saved Wireless Debugging routine yet.' : `${destinationLabel(destination)} has no saved routine yet.`);
  }, [destination, savedProfiles, screen, wireless]);

  const recordAction = async (action, insertAt = null) => {
    const resolvedInsertAt = Number.isInteger(insertAt)
      ? insertAt
      : Number.isInteger(stepIndex)
        ? Math.min(stepIndex + 1, actions.length)
        : null;
    setResult(null);
    setHasUnsavedChanges(true);
    setActions((current) => {
      if (!Number.isInteger(resolvedInsertAt)) return [...current, action];
      const next = [...current];
      next.splice(Math.max(0, Math.min(resolvedInsertAt, next.length)), 0, action);
      return next;
    });
    setStepCheckpoints((current) => {
      if (!Number.isInteger(resolvedInsertAt)) return [...current, null];
      const next = [...current];
      next.splice(Math.max(0, Math.min(resolvedInsertAt, next.length)), 0, null);
      return next;
    });
    if (Number.isInteger(stepIndex)) setStepIndex(resolvedInsertAt);
    if (!sendWhileRecording || !screen?.televisionDeviceId) return;
    setLiveAction(action);
    setMessage(`Sending ${action} to ${screen.name}…`);
    try {
      const response = await axios.post(
        `/api/site-control/targets/${screen.televisionDeviceId}/action`,
        inputTrainingActionBody(action, controlMethods, inputSelectorMethod, wireless),
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      setMessage(`${action} sent to ${screen.name}${inputDeliverySummary(response.data)} and added to the command flow.`);
    } catch (error) {
      setMessage(`${action} was recorded, but live control failed: ${error?.response?.data?.message || error.message || 'command failed'}${inputFailureSummary(error)}`);
    } finally {
      setLiveAction('');
    }
  };

  const recordWait = (durationMs = 2000, insertAt = null) => {
    const waitToken = createWaitToken(durationMs);
    const resolvedInsertAt = Number.isInteger(insertAt)
      ? insertAt
      : Number.isInteger(stepIndex)
        ? Math.min(stepIndex + 1, actions.length)
        : null;
    setResult(null);
    setHasUnsavedChanges(true);
    setActions((current) => {
      if (!Number.isInteger(resolvedInsertAt)) return [...current, waitToken];
      const next = [...current];
      next.splice(Math.max(0, Math.min(resolvedInsertAt, next.length)), 0, waitToken);
      return next;
    });
    setStepCheckpoints((current) => {
      if (!Number.isInteger(resolvedInsertAt)) return [...current, null];
      const next = [...current];
      next.splice(Math.max(0, Math.min(resolvedInsertAt, next.length)), 0, null);
      return next;
    });
    if (Number.isInteger(stepIndex)) setStepIndex(resolvedInsertAt);
    setMessage('Wait 2s added. Tap the wait step to set a different duration.');
  };

  const editWait = (index) => {
    const currentMs = waitMsFromToken(actions[index]);
    if (currentMs == null) return;
    if (busy || liveAction) return;
    const value = window.prompt('Wait duration in seconds (0.1–30)', String(currentMs / 1000));
    if (value == null) return;
    const seconds = Number(value);
    if (!Number.isFinite(seconds)) {
      setMessage('Enter a wait duration between 0.1 and 30 seconds.');
      return;
    }
    const durationMs = normalizeWaitMs(seconds * 1000);
    setActions((current) => current.map((entry, itemIndex) => (
      itemIndex === index ? createWaitToken(durationMs) : entry
    )));
    setResult(null);
    setHasUnsavedChanges(true);
    setMessage(`${formatSequenceStep(createWaitToken(durationMs))} saved in the command flow.`);
  };

  const removeStep = (index) => {
    if (busy || liveAction) return;
    setActions((current) => current.filter((_, itemIndex) => itemIndex !== index));
    setStepCheckpoints((current) => current.filter((_, itemIndex) => itemIndex !== index));
    setStepIndex(null);
    setResult(null);
    setHasUnsavedChanges(true);
  };

  const executeNextStep = async () => {
    if (!screen?.televisionDeviceId || !actions.length) {
      setMessage('Record at least one command before stepping through the routine.');
      return;
    }
    const nextIndex = !Number.isInteger(stepIndex) || stepIndex >= actions.length - 1
      ? 0
      : stepIndex + 1;
    const action = actions[nextIndex];
    const waitMs = waitMsFromToken(action);
    const label = formatSequenceStep(action);
    let delivery = '';
    setBusy('step');
    setLiveAction(waitMs == null ? action : '');
    setStepIndex(nextIndex);
    setMessage(waitMs == null
      ? `Step ${nextIndex + 1}: sending ${label} to ${screen.name}…`
      : `Step ${nextIndex + 1}: ${label.toLowerCase()}…`);
    try {
      if (waitMs != null) {
        await new Promise((resolve) => window.setTimeout(resolve, waitMs));
      } else {
        const response = await axios.post(
          `/api/site-control/targets/${screen.televisionDeviceId}/action`,
          inputTrainingActionBody(action, controlMethods, inputSelectorMethod, wireless),
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
        );
        delivery = inputDeliverySummary(response.data);
      }
      setMessage(`Step ${nextIndex + 1} completed: ${label}${delivery}. Press Step → to continue, or insert the next command or checkpoint here.`);
    } catch (error) {
      setMessage(`Step ${nextIndex + 1} failed: ${error?.response?.data?.message || error.message || 'command failed'}${inputFailureSummary(error)}`);
    } finally {
      setLiveAction('');
      setBusy('');
    }
  };

  const captureCheckpoint = async (stepIndex) => {
    if (!screen?.televisionDeviceId) return;
    setBusy('checkpoint');
    setMessage('Capturing the current TV screen for a visual checkpoint…');
    try {
      const response = await axios.post(
        `/api/site-control/targets/${screen.televisionDeviceId}/checkpoint-state`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      setCheckpointEditor({ stepIndex, screenshotDataUrl: response.data.screenshotDataUrl });
      setMessage('Draw around a stable part of the screen that proves navigation reached this point.');
    } catch (error) {
      setMessage(error?.response?.data?.message || error.message || 'Could not capture a checkpoint screenshot.');
    } finally {
      setBusy('');
    }
  };

  const addCheckpointAt = (destinationIndex) => {
    if (!actions.length) {
      setMessage('Add at least one command before adding a checkpoint. A checkpoint observes the result of the preceding command.');
      return;
    }
    if (destinationIndex < 1) {
      setMessage('Place the checkpoint after a command so there is a TV state to observe.');
      return;
    }
    const stepIndex = Math.min(destinationIndex - 1, actions.length - 1);
    if (stepCheckpoints[stepIndex]) {
      setMessage('There is already a checkpoint at this point in the flow. Remove it before capturing a replacement.');
      return;
    }
    captureCheckpoint(stepIndex);
  };

  const saveCheckpoint = (checkpoint) => {
    setStepCheckpoints((current) => current.map((entry, index) => index === checkpointEditor.stepIndex ? checkpoint : entry));
    setCheckpointEditor(null);
    setResult(null);
    setHasUnsavedChanges(true);
    setMessage('Visual checkpoint added. Test the routine to verify its crop and recovery behavior.');
  };

  const setDragData = (event, payload) => {
    event.dataTransfer.effectAllowed = payload.source === 'palette' ? 'copy' : 'move';
    event.dataTransfer.setData('application/x-soundscreen-command', JSON.stringify(payload));
  };

  const readDragData = (event) => {
    try {
      return JSON.parse(event.dataTransfer.getData('application/x-soundscreen-command'));
    } catch {
      return null;
    }
  };

  const dropCommand = (event, destinationIndex) => {
    event.preventDefault();
    if (busy || liveAction) return;
    const dragged = readDragData(event);
    if (!dragged) return;
    if (dragged.source === 'palette' && dragged.kind === 'checkpoint') {
      addCheckpointAt(destinationIndex);
      return;
    }
    if (dragged.source === 'palette' && dragged.kind === 'wait') {
      recordWait(dragged.durationMs, destinationIndex);
      return;
    }
    if (dragged.source === 'palette' && ACTIONS.includes(dragged.action)) {
      recordAction(dragged.action, destinationIndex);
      return;
    }
    if (dragged.source !== 'sequence' || !Number.isInteger(dragged.index)) return;
    setActions((current) => {
      if (dragged.index < 0 || dragged.index >= current.length) return current;
      const next = [...current];
      const [command] = next.splice(dragged.index, 1);
      const adjustedIndex = dragged.index < destinationIndex ? destinationIndex - 1 : destinationIndex;
      next.splice(Math.max(0, Math.min(adjustedIndex, next.length)), 0, command);
      return next;
    });
    setStepCheckpoints((current) => {
      const next = [...current];
      const [checkpoint] = next.splice(dragged.index, 1);
      const adjustedIndex = dragged.index < destinationIndex ? destinationIndex - 1 : destinationIndex;
      next.splice(Math.max(0, Math.min(adjustedIndex, next.length)), 0, checkpoint || null);
      return next;
    });
    setResult(null);
    setHasUnsavedChanges(true);
    setStepIndex(null);
    setMessage('Command order updated. Test the edited routine again before enabling it.');
  };

  const callRunner = async (dryRun) => {
    if (!screen || !destination || !actions.length) {
      setMessage(wireless ? 'Record at least one recovery action first.' : 'Choose an input destination and record at least one action first.');
      return;
    }
    setBusy(dryRun ? 'plan' : 'execute');
    setMessage('');
    setResult(null);
    try {
      const response = await axios.post(
        `/api/site-control/targets/${screen.televisionDeviceId}/automation`,
        { profile, operation: expectedPort ? { expectedPort } : {}, dryRun },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      setResult(response.data);
      setMessage(resultSummary(response.data, wireless));
    } catch (error) {
      setMessage(`${error?.response?.data?.message || error.message || 'Automation request failed.'}${inputFailureSummary(error)}`);
    } finally {
      setBusy('');
    }
  };

  const copyRecoveryRoutine = async (targetId) => {
    if (!targetId) return;
    setBusy('copy');
    try {
      const response = await axios.get(`/api/site-control/targets/${targetId}/wireless-debugging-routine`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (!response.data?.profile) throw new Error('That device has no saved recovery routine.');
      loadProfile({ ...response.data.profile, trainingState: 'draft', validation: null });
      setHasUnsavedChanges(true);
      setMessage('Recovery keys copied as a draft. Test on this device before saving and enabling.');
    } catch (error) { setMessage(error?.response?.data?.message || error.message); }
    finally { setBusy(''); }
  };

  const saveRoutine = async (candidate = profile) => {
    if (wireless) {
      if (!candidate || !actions.length) { setMessage('Record at least one recovery action before saving.'); return; }
      setBusy('save');
      try {
        const saved = await onSaveRecovery(candidate);
        loadProfile(saved);
        setMessage(saved.trainingState === 'validated' ? 'Wireless Debugging routine enabled for manual reconnect.' : 'Wireless Debugging routine saved. Test and enable it before using Reconnect.');
      } catch (error) { setMessage(error?.response?.data?.message || error.message || 'Could not save the routine.'); }
      finally { setBusy(''); }
      return;
    }
    if (!screen || !destination || !actions.length || !candidate) {
      setMessage('Choose an input destination and record at least one action before saving.');
      return;
    }
    const existingProfiles = screen.activationStrategy?.automationProfiles || [];
    const candidateDestination = profileInputDestination(candidate) || destination;
    const nextProfiles = upsertInputProfileByPort(existingProfiles, candidate);
    const existingMethods = screen.activationStrategy?.inputSwitching?.methods || [];
    const nextMethods = candidate.trainingState === 'validated'
      ? ['trained_menu', ...existingMethods.filter((method) => method !== 'trained_menu')]
      : existingMethods;
    const nextTopology = {
      ...topology,
      screens: topology.screens.map((entry) => entry.id !== screen.id ? entry : ({
        ...entry,
        activationStrategy: {
          ...entry.activationStrategy,
          automationProfiles: nextProfiles,
          inputSwitching: {
            ...entry.activationStrategy?.inputSwitching,
            methods: nextMethods,
            defaultMethod: candidate.trainingState === 'validated'
              ? 'trained_menu' : (entry.activationStrategy?.inputSwitching?.defaultMethod || nextMethods[0] || null),
          },
        },
      })),
    };
    setBusy('save');
    try {
      const response = await axios.put(`/api/areas/${areaId}/topology`, nextTopology, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      const persistedScreen = (response.data?.screens || []).find((entry) => (
        Number(entry.televisionDeviceId) === Number(screen.televisionDeviceId)
      ));
      const persistedProfile = (persistedScreen?.activationStrategy?.automationProfiles || [])
        .find((entry) => profileInputDestination(entry) === candidateDestination);
      if (!persistedProfile) {
        throw new Error('The server saved the Screen topology but did not return this routine.');
      }
      if (candidate.trainingState === 'validated' && persistedProfile.trainingState !== 'validated') {
        throw new Error('The server did not retain the enabled state for this routine.');
      }
      onTopologyChange?.(response.data);
      setScreenId(String(persistedScreen.id));
      loadProfile(persistedProfile);
      setMessage(candidate.trainingState === 'validated'
        ? `${candidate.name} enabled as a verified routine.`
        : `${candidate.name} saved. It remains unavailable to automatic scenes until a successful test is explicitly confirmed later.`);
      setHasUnsavedChanges(false);
    } catch (error) {
      setMessage(error?.response?.data?.message || error.message || 'Could not save the routine.');
    } finally {
      setBusy('');
    }
  };

  const deleteRoutine = async () => {
    if (wireless) {
      if (!window.confirm('Delete the Wireless Debugging routine?')) return;
      setBusy('delete');
      try {
        await onSaveRecovery(null);
        setActions([]); setStepCheckpoints([]); setResult(null); setHasUnsavedChanges(false);
        setMessage('Wireless Debugging routine deleted.');
      } catch (error) { setMessage(error?.response?.data?.message || error.message); }
      finally { setBusy(''); }
      return;
    }
    if (!screen || !destination) return;
    if (!savedProfile || !window.confirm(`Delete the ${destinationLabel(destination)} input routine?`)) return;
    const nextTopology = {
      ...topology,
      screens: topology.screens.map((entry) => entry.id !== screen.id ? entry : ({
        ...entry,
        activationStrategy: {
          ...entry.activationStrategy,
          automationProfiles: (entry.activationStrategy?.automationProfiles || [])
            .filter((entry) => entry.goal !== 'input_selector' || profileInputDestination(entry) !== destination),
        },
      })),
    };
    setBusy('delete');
    try {
      const response = await axios.put(`/api/areas/${areaId}/topology`, nextTopology, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      onTopologyChange?.(response.data);
      setActions([]);
      setStepCheckpoints([]);
      setHasUnsavedChanges(false);
      setMessage(`${destinationLabel(destination)} routine deleted.`);
    } catch (error) {
      setMessage(error?.response?.data?.message || error.message || 'Could not delete the routine.');
    } finally {
      setBusy('');
    }
  };

  const cloneFromDestination = (sourceDestination) => {
    setClonePort(sourceDestination);
    if (!sourceDestination) return;
    const normalizedSource = sourceDestination === 'home' ? 'home' : Number(sourceDestination);
    const sourceProfile = savedProfiles.find((entry) => profileInputDestination(entry) === normalizedSource);
    if (!sourceProfile) {
      setMessage(`${destinationLabel(normalizedSource)} no longer has a saved routine to clone.`);
      setClonePort('');
      return;
    }
    loadProfile(sourceProfile);
    setHasUnsavedChanges(true);
    setClonePort('');
    setMessage(`Cloned ${destinationLabel(normalizedSource)} into ${destinationLabel(destination)}. Adjust the steps, then save and test ${destinationLabel(destination)}.`);
  };

  const copyFromArea = async (sourceAreaId) => {
    setCopyAreaId(sourceAreaId);
    if (!sourceAreaId) return;
    setCopyBusy(true);
    try {
      const response = await axios.get(`/api/areas/${sourceAreaId}/topology`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      const sourceProfiles = (response.data?.screens || [])
        .filter((entry) => entry.displayKind !== 'unmanaged' && entry.televisionDeviceId)
        .flatMap((entry) => entry.activationStrategy?.automationProfiles || [])
        .filter((entry) => entry.goal === 'input_selector' && profileInputDestination(entry) === destination);
      if (!sourceProfiles[0]) throw new Error(`No ${destinationLabel(destination)} routine was found in that area.`);
      loadProfile(sourceProfiles[0]);
      setHasUnsavedChanges(true);
      const sourceArea = siteAreas.find((entry) => String(entry.id) === String(sourceAreaId));
      setMessage(`Copied ${destinationLabel(destination)} from ${sourceArea?.name || 'the selected area'}. Test it, then save.`);
    } catch (error) {
      setMessage(error?.response?.data?.message || error.message || 'Could not copy the routine.');
    } finally {
      setCopyBusy(false);
      setCopyAreaId('');
    }
  };

  const enableRoutine = (installerConfirmed = false) => {
    if ((!telemetryConfirmed && !installerConfirmed) || !profile) return;
    if (installerConfirmed && !window.confirm(wireless ? 'Enable this routine after watching Wireless Debugging turn on? Test again with ADB unavailable before relying on it.' : 'Enable this routine without telemetry confirmation? Use this only after watching the TV switch to the correct input.')) return;
    const evidence = result?.finalEvaluation?.observations?.find((entry) => entry.type === 'tv_active_input');
    saveRoutine({
      ...profile,
      trainingState: 'validated',
      validation: {
        validatedAt: new Date().toISOString(),
        verificationMethod: installerConfirmed ? 'installer_observation' : wireless ? 'adb_connected' : 'tv_active_input',
        result: installerConfirmed ? 'installer_confirmed' : 'verified',
        evidenceSource: installerConfirmed
          ? 'installer'
          : wireless ? 'adb_connection_test' : evidence?.source || liveActiveSource?.adapter || 'android.media.tv.TvInputManager',
      },
    });
  };

  const testInputOpener = async (method) => {
    if (!screen?.televisionDeviceId) return;
    setBusy(`opener-${method}`);
    setInputSelectorMethod(method);
    setControlMethods(controlMethods || DEFAULT_INPUT_CONTROL_METHODS);
    setResult(null);
    setHasUnsavedChanges(true);
    const methodName = method === 'suitestream_intent' ? 'Suitestream selector intent' : method === 'adb_remote' ? 'ADB Input key' : 'ATV Input key';
    setMessage(`Testing ${methodName}…`);
    try {
      await axios.post(
        `/api/site-control/targets/${screen.televisionDeviceId}/action`,
        {
          ...inputTrainingActionBody('input', controlMethods || DEFAULT_INPUT_CONTROL_METHODS, method),
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      setMessage(`${methodName} sent. If it worked, keep this opener selected and test the full routine before enabling it.`);
    } catch (error) {
      setMessage(`${error?.response?.data?.message || error.message || 'Input opener test failed.'}${inputFailureSummary(error)}`);
    } finally {
      setBusy('');
    }
  };

  const changeControlMethod = (index, method) => {
    if (index === 0 && method === 'lg_webos_remote') { setControlMethods(['lg_webos_remote']); setInputSelectorMethod('control_order'); return; }
    const current = controlMethods || DEFAULT_INPUT_CONTROL_METHODS;
    const before = current.slice(0, index);
    const after = current.slice(index + 1).filter((value) => value !== method);
    setControlMethods(method ? [...before, method, ...after] : before);
    setResult(null);
    setHasUnsavedChanges(true);
    setStepIndex(null);
  };

  if (!managedScreens.length) {
    const configuredScreens = topology?.screens || [];
    const missingTelevision = configuredScreens.some((entry) => (
      entry.displayKind !== 'unmanaged' && !entry.televisionDeviceId
    ));
    return (
      <section className="input-automation-trainer input-automation-trainer--empty">
        <div className="input-automation-trainer__heading">
          <div>
            <h3>Input-selector trainer</h3>
            <p>{missingTelevision
              ? 'This screen has no television control target assigned, so remote training cannot start.'
              : 'Add a managed screen and assign its television control target before training an input routine.'}</p>
          </div>
          <strong>Setup required</strong>
        </div>
        {onConfigureScreen && (
          <div className="input-automation-trainer__controls">
            <button type="button" onClick={onConfigureScreen}>Configure screen in Virtual Matrix</button>
          </div>
        )}
      </section>
    );
  }
  return (
    <section className="input-automation-trainer">
      <div className="input-automation-trainer__heading">
        <div>
          <h3>{wireless ? 'Wireless Debugging trainer' : 'Input-selector trainer'}</h3>
          <p>{wireless ? 'Record Settings navigation over Android TV Remote. Start from a repeatable screen, include menu-loading waits, then test with ADB unavailable. Recovery stays manual.' : 'Record a short remote sequence, inspect its dry-run plan, then deliberately test it against TV telemetry.'}</p>
          {wireless && <p>Visual checkpoints need a working snapshot source while ADB is down. Watch the device when testing; a toggle can turn Wireless Debugging off if the starting state differs.</p>}
        </div>
        <strong>Training</strong>
      </div>
      <div className="input-automation-trainer__targets">
        <label>{wireless ? 'Control target' : 'Television'}
          <select disabled={Boolean(busy || liveAction)} value={screen?.id || ''} onChange={(event) => { setScreenId(event.target.value); setTargetPort(''); }}>
            {managedScreens.map((entry) => <option key={entry.id} value={entry.id}>{entry.name}</option>)}
          </select>
        </label>
        {!wireless && <label>Input destination
          <select disabled={Boolean(busy || liveAction)} value={destination || ''} onChange={(event) => { setTargetPort(event.target.value); setResult(null); }}>
            {!availablePorts.length && <option value="">No HDMI ports discovered</option>}
            <option value="home">TV Home · optional</option>
            {availablePorts.map((port) => {
              const mappedPlayer = players.find((entry) => hdmiPort(entry.expectedInput) === port);
              const trained = savedProfiles.some((entry) => profileInputDestination(entry) === port);
              return <option key={port} value={port}>HDMI {port}{mappedPlayer?.name ? ` · ${mappedPlayer.name}` : ''}{trained ? ' · saved' : ''}</option>;
            })}
          </select>
        </label>}
        <div className="input-automation-trainer__routine-status">
          <span>Routine</span>
          <strong>{hasUnsavedChanges
            ? 'Changed · testing required'
            : savedProfile
              ? (savedProfile.trainingState === 'validated' ? 'Enabled' : 'Saved · testing required')
              : 'Not configured'}</strong>
        </div>
        {wireless && <label>Copy From
          <select value="" disabled={Boolean(busy || liveAction)} onChange={(event) => copyRecoveryRoutine(event.target.value)}>
            <option value="">Same-site recovery routine…</option>
            {recoveryTargets.filter((entry) => Number(entry.deviceId) !== Number(recoveryTarget.id)).map((entry) => (
              <option key={entry.deviceId} value={entry.deviceId}>{entry.name}</option>
            ))}
          </select>
        </label>}
        {!wireless && <>
          <label>Primary control method
            <select aria-label="Primary control method" disabled={Boolean(busy || liveAction)} value={controlMethods?.[0] || 'legacy'}
              onChange={(event) => changeControlMethod(0, event.target.value)}>
              {!controlMethods && <option value="legacy">Existing automatic routing</option>}
              {INPUT_CONTROL_METHODS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </label>
          {controlMethods && controlMethods[0] !== 'lg_webos_remote' && [1, 2].map((index) => <label key={index}>Fallback {index}
            <select aria-label={`Fallback ${index}`} disabled={Boolean(busy || liveAction) || !controlMethods[index - 1]}
              value={controlMethods[index] || ''} onChange={(event) => changeControlMethod(index, event.target.value)}>
              <option value="">None</option>
              {INPUT_CONTROL_METHODS.filter(([value]) => value !== 'lg_webos_remote' && !controlMethods.slice(0, index).includes(value))
                .map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </label>)}
          <label>Input opener
            <select
              aria-label="Input opener"
              disabled={Boolean(busy || liveAction)}
              value={inputSelectorMethod}
              onChange={(event) => {
                setInputSelectorMethod(event.target.value);
                setControlMethods(controlMethods || DEFAULT_INPUT_CONTROL_METHODS);
                setResult(null);
                setHasUnsavedChanges(true);
              }}
            >
              <option value="control_order">Use control method order</option>
              <option value="lg_webos">LG webOS Input key</option>
              {controlMethods?.[0] !== 'lg_webos_remote' && <option value="suitestream_intent">Suitestream selector intent</option>}
              {controlMethods?.[0] !== 'lg_webos_remote' && <option value="android_tv_remote">ATV Remote Input key</option>}
              {controlMethods?.[0] !== 'lg_webos_remote' && <option value="adb_remote">ADB Remote Input key</option>}
            </select>
          </label>
          <div className="input-automation-trainer__routine-status">
            <span>Test opener</span>
            <button type="button" disabled={Boolean(busy || liveAction) || controlMethods?.[0] === 'lg_webos_remote'} onClick={() => testInputOpener('suitestream_intent')}>
              {busy === 'opener-suitestream_intent' ? 'Testing…' : 'Test Suitestream'}
            </button>
            <button type="button" disabled={Boolean(busy || liveAction) || controlMethods?.[0] === 'lg_webos_remote'} onClick={() => testInputOpener('android_tv_remote')}>
              {busy === 'opener-android_tv_remote' ? 'Testing…' : 'Test ATV'}
            </button>
            <button type="button" disabled={Boolean(busy || liveAction) || controlMethods?.[0] === 'lg_webos_remote'} onClick={() => testInputOpener('adb_remote')}>
              {busy === 'opener-adb_remote' ? 'Testing…' : 'Test ADB'}
            </button>
                      {controlMethods?.[0] === 'lg_webos_remote' && <button type="button" disabled={Boolean(busy || liveAction)} onClick={() => testInputOpener('lg_webos')}>Test LG Input</button>}
          </div>
          <label>Clone From
            <select value={clonePort} onChange={(event) => cloneFromDestination(event.target.value)} disabled={!destination}>
              <option value="">Another input routine…</option>
              {savedProfiles
                .filter((entry) => profileInputDestination(entry) && profileInputDestination(entry) !== destination)
                .sort((left, right) => String(profileInputDestination(left)).localeCompare(String(profileInputDestination(right)), undefined, { numeric: true }))
                .map((entry) => (
                  <option key={entry.id} value={profileInputDestination(entry)}>{destinationLabel(profileInputDestination(entry))}</option>
                ))}
            </select>
          </label>
          <label>Copy From
            <select value={copyAreaId} onChange={(event) => copyFromArea(event.target.value)} disabled={copyBusy || !destination}>
              <option value="">Same-site configured TV…</option>
              {siteAreas.filter((entry) => String(entry.id) !== String(areaId)).map((entry) => (
                <option key={entry.id} value={entry.id}>{entry.name}</option>
              ))}
            </select>
          </label>
        </>}
        <label>Playback step delay
          <select disabled={Boolean(busy || liveAction)} value={stepDelayMs} onChange={(event) => { setStepDelayMs(Number(event.target.value)); setResult(null); setHasUnsavedChanges(true); }}>
            <option value={150}>Very fast · 0.15s</option>
            <option value={300}>Fast · 0.3s</option>
            <option value={600}>Normal · 0.6s</option>
            <option value={1000}>Slow · 1s</option>
            <option value={1500}>Very slow · 1.5s</option>
            <option value={2500}>Diagnostic · 2.5s</option>
          </select>
        </label>
      </div>
      {!wireless && <p>{controlMethods
        ? `Keys use ${controlMethods.map(inputControlMethodLabel).join(' → ')}. Fallbacks run only when the previous method did not deliver. An explicit input opener is tested on its own.`
        : 'This saved routine uses existing automatic routing. Choose a primary method and retest to save an explicit control order.'}</p>}
      <label className="input-automation-trainer__live-toggle">
        <input type="checkbox" disabled={Boolean(busy || liveAction)} checked={sendWhileRecording} onChange={(event) => setSendWhileRecording(event.target.checked)} />
        Send each action to the selected {wireless ? 'device via Android TV Remote' : 'TV'} while recording
      </label>
      <div className="input-automation-trainer__palette">
        {ACTIONS.map((action) => <button type="button" key={action} draggable={!liveAction && !busy} disabled={Boolean(liveAction || busy)} onDragStart={(event) => setDragData(event, { source: 'palette', action })} onClick={() => recordAction(action)}>+ {liveAction === action ? 'sending…' : action}</button>)}
        <button type="button" className="input-automation-trainer__wait-palette" draggable={!busy} disabled={Boolean(busy || liveAction)} onDragStart={(event) => setDragData(event, { source: 'palette', kind: 'wait', durationMs: 2000 })} onClick={() => recordWait()}>+ Wait 2s</button>
        <button type="button" className="input-automation-trainer__checkpoint-palette" draggable={!busy} disabled={Boolean(busy || liveAction)} onDragStart={(event) => setDragData(event, { source: 'palette', kind: 'checkpoint' })} onClick={() => addCheckpointAt(Number.isInteger(stepIndex) ? stepIndex + 1 : actions.length)}>+ Checkpoint</button>
      </div>
      <div className="input-automation-trainer__sequence" onDragOver={(event) => event.preventDefault()} onDrop={(event) => dropCommand(event, actions.length)}>
        {actions.length ? actions.map((action, index) => (
          <React.Fragment key={`${action}-${index}`}>
          <div className={`input-automation-trainer__step ${isWaitToken(action) ? 'is-wait' : ''} ${stepIndex === index ? 'is-stepping' : ''}`}>
            <div className="input-automation-trainer__step-row">
              <button type="button" disabled={Boolean(busy || liveAction)} draggable={!busy && !liveAction} onDragStart={(event) => { event.stopPropagation(); setDragData(event, { source: 'sequence', index }); }} onDragOver={(event) => { event.preventDefault(); event.stopPropagation(); }} onDrop={(event) => { event.stopPropagation(); dropCommand(event, index); }} onClick={() => isWaitToken(action) ? editWait(index) : removeStep(index)} title={isWaitToken(action) ? 'Drag to reorder, or click to edit' : 'Drag to reorder, or click to remove'}>
                <small>{index + 1}</small>{formatSequenceStep(action)}<span>{isWaitToken(action) ? 'edit' : '×'}</span>
              </button>
              {isWaitToken(action) ? <button type="button" className="input-automation-trainer__remove-wait" disabled={Boolean(busy || liveAction)} onClick={() => removeStep(index)} title="Remove wait">×</button> : null}
            </div>
          </div>
          {stepCheckpoints[index] ? (
            <button
              type="button"
              disabled={Boolean(busy || liveAction)}
              className={`input-automation-trainer__checkpoint-step ${stepIndex === index ? 'is-stepping' : ''}`}
              onDragOver={(event) => { event.preventDefault(); event.stopPropagation(); }}
              onDrop={(event) => { event.stopPropagation(); dropCommand(event, index + 1); }}
              onClick={() => { setStepCheckpoints((current) => current.map((entry, itemIndex) => itemIndex === index ? null : entry)); setResult(null); setHasUnsavedChanges(true); setMessage('Checkpoint removed from the command flow.'); }}
              title="Visual checkpoint. Click to remove."
            >
              <small>✓</small>Checkpoint<span>×</span>
            </button>
          ) : null}
          </React.Fragment>
        )) : <p>No actions recorded. Click actions above in the order the TV needs them.</p>}
      </div>
      <div className="input-automation-trainer__controls">
        {wireless && <button type="button" onClick={() => callRunner(true)} disabled={Boolean(busy || liveAction) || !actions.length}>Dry run</button>}
        <button type="button" className="input-automation-trainer__step-runner" onClick={executeNextStep} disabled={Boolean(busy || liveAction) || !actions.length}>{busy === 'step' ? 'Stepping…' : 'Step →'}</button>
        <button type="button" onClick={() => saveRoutine()} disabled={Boolean(busy || liveAction) || !destination || !hasUnsavedChanges}>Save</button>
        <button type="button" className="input-automation-trainer__execute" onClick={() => callRunner(false)} disabled={Boolean(busy || liveAction) || !destination || !actions.length}>
          {busy === 'execute' ? 'Testing…' : wireless ? 'Test Wireless Debugging recovery' : `Test on TV → ${destinationLabel(destination)}`}
        </button>
        {telemetryConfirmed && <button type="button" className="input-automation-trainer__enable" onClick={() => enableRoutine(false)} disabled={Boolean(busy || liveAction)}>That worked — enable this routine</button>}
        {executionCompleted && !telemetryConfirmed && <button type="button" className="input-automation-trainer__enable-manual" onClick={() => enableRoutine(true)} disabled={Boolean(busy || liveAction)}>Enable with installer confirmation</button>}
        {savedProfile && <button type="button" onClick={deleteRoutine} disabled={Boolean(busy || liveAction)}>Delete {wireless ? 'Wireless Debugging' : destinationLabel(destination)} routine</button>}
      </div>
      {!hasUnsavedChanges && savedProfile?.trainingState === 'validated' && <p className="input-automation-trainer__message is-success">Enabled · changes require another test before enabling again.</p>}
      {message && <p className={`input-automation-trainer__message ${result?.status === 'verified' ? 'is-success' : ''}`}>{message}</p>}
      {result?.results?.some((entry) => entry.inputControlAttempts) && <ul aria-label="Methods used">
        {result.results.filter((entry) => entry.inputControlAttempts).map((entry, index) => <li key={index}>
          {entry.action}: {entry.inputControlAttempts.map((attempt) => `${inputControlMethodLabel(attempt.controlMethod)} (${attempt.status})`).join(' → ')}
        </li>)}
      </ul>}
      {result && <details><summary>Checkpoint details</summary><pre>{JSON.stringify(result, null, 2)}</pre></details>}
      {checkpointEditor && <VisualCheckpointEditor screenshotDataUrl={checkpointEditor.screenshotDataUrl} stepIndex={checkpointEditor.stepIndex} routinePrefix={actions.slice(0, checkpointEditor.stepIndex + 1)} onSave={saveCheckpoint} onCancel={() => setCheckpointEditor(null)} />}
    </section>
  );
}
